import React, { ReactElement, useRef } from 'react';
import { Box, chakra } from '@chakra-ui/react';
import { useMultiStyleConfig } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import merge from 'lodash/merge';

import { getCurrencyFormatter, getGidFromIdForResource } from '../../../utils';
import MinusIcon from '../../../components/Icons/MinusIcon';
import PlusIcon from '../../../components/Icons/PlusIcon';
import StarIcon from '../../../components/Icons/Star';
import { H5 } from '../../../components/Typography';
import { SlidePanelProductInfoProps } from '../../../components/SlidePanel';
import LazyImage from '../../../components/LazyImage';
import { useInventory } from '../../../subscriptions/state/InventoryContext';
import { isThereEnoughInventory } from '../../../subscriptions/utils';
import { useBundleSetting } from '../../../subscriptions/state/BundleSettingContext';
import { applyDiscountsToLine } from '../../../subscriptions/utils';
import { FinalFocusRef } from '../../../components/SlidePanel/SlidePanel';
import { parseProductCardStyles, useIdStyling } from '../../theme/themeUtils';

const starColor = '#ffc600';

const CustomMinusIcon = chakra(MinusIcon);
const CustomPlusIcon = chakra(PlusIcon);

const Reviews = ({
  reviews,
  onClick,
  onKeyDown,
}: {
  reviews: Reviews;
  onClick: (finalFocusRef: FinalFocusRef) => void;
  onKeyDown: (e: React.KeyboardEvent, finalFocusRef: FinalFocusRef) => void;
}) => {
  const stars = Array.from(
    { length: Math.ceil(Number(reviews?.average) || 0) },
    (_, i) => i + 1,
  );
  const { t } = useTranslation();
  const starsRef = useRef<HTMLDivElement>(null);

  const styles = useMultiStyleConfig('ProductCard', {});

  return (
    <Box
      __css={styles.StarsContainer}
      className="bundle-product-card__stars-container"
      role="button"
      aria-label={t('aria_labels.stars_number', { stars_qty: stars.length })}
      tabIndex={0}
      onKeyDown={(e) => onKeyDown(e, starsRef)}
      onClick={() => onClick(starsRef)}
      ref={starsRef}
    >
      {stars.map((star) => (
        <StarIcon fill={starColor} key={star}></StarIcon>
      ))}
    </Box>
  );
};

export interface ProductCardProps {
  children?: React.ReactElement;
  onChoose?: (product: GlobalProduct['id']) => void;
  onIncrease?: () => void;
  onDecrease?: () => void;
  onCardClick?: (productInfo: SlidePanelProductInfoProps) => void;
  onChooseText?: string;
  price?: string | number;
  currency?: string;
  id?: number | string;
  handle?: string;
  productId?: number | string;
  image: string | null;
  quantity?: number;
  currentPrice?: number;
  title: string;
  subtitle?: string;
  variantTitle?: string;
  available?: boolean;
  description?: string;
  VariantSelector?: JSX.Element | null;
  doNotApplyDiscount?: boolean;
  reviews?: Reviews;
  _awtId?: string;
}

const getOptimisedImageUrl = (image: string | null, size: number) =>
  image ? `${image}&width=${size}` : null;

const ThemedProductCard: React.FC<ProductCardProps> = ({
  id,
  handle,
  productId,
  image,
  quantity = 0,
  price,
  currentPrice,
  title,
  subtitle,
  variantTitle,
  currency,
  available,
  onChoose,
  onChooseText,
  children,
  onIncrease,
  onDecrease,
  onCardClick,
  description,
  VariantSelector,
  doNotApplyDiscount = false,
  reviews,
  _awtId,
}) => {
  const { t } = useTranslation();
  const { inventory } = useInventory();
  const { bundleSetting } = useBundleSetting();

  const productVariantId = getGidFromIdForResource(
    id as string,
    'ProductVariant',
  );
  const productVariantInventory = inventory?.[productVariantId];
  const productImage = window?.bundleapp?.settings?.bab?.productImage || 220;
  const variantTitleRef = useRef<HTMLHeadingElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLHeadingElement>(null);

  const styleOverrides = useIdStyling(_awtId);
  const baseStyles = useMultiStyleConfig('ProductCard', {});
  const parsedStyleOverrides = parseProductCardStyles(styleOverrides);

  const mergedStyles = merge({}, baseStyles, parsedStyleOverrides);

  const handleClick = (finalFocusRef: FinalFocusRef) => {
    if (onCardClick) {
      onCardClick({
        productId,
        image,
        title,
        variantTitle,
        subtitle,
        description,
        reviews,
        handle,
        id,
        finalFocusRef,
      });
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    finalFocusRef: FinalFocusRef,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();

      handleClick(finalFocusRef);
    }
  };

  if (!id) return null;
  return (
    <Box
      __css={mergedStyles.CardContainer}
      className={`bundle-product-card p_${id} q_${quantity}${
        quantity > 0 ? ' selected' : ''
      } ${
        available || available === undefined
          ? 'bundle-product-card--available'
          : 'bundle-product-card--unavailable'
      }`}
      data-testid="product-card"
      data-pid={id}
      data-quantity={quantity}
      data-available={available}
      role="gridcell"
    >
      <Box
        data-testid="product-card-clickable-area"
        style={{ cursor: onCardClick ? 'pointer' : 'unset' }}
        onClick={() => handleClick(titleRef)}
        aria-label={t('aria_labels.product_card', { title })}
        role="button"
        __css={mergedStyles.imageContainer}
      >
        {mergedStyles.showBadgeWhenSelected && quantity > 0 && (
          <Box
            as="span"
            className="bundle-product-card__badge"
            data-testid="product-badge"
            __css={mergedStyles.Badge}
            aria-label={t('aria_labels.product_selected', { title })}
          >
            {mergedStyles.badgeText || t('product_swap.in_box')}
          </Box>
        )}
        <LazyImage
          image={getOptimisedImageUrl(image, productImage)}
          thumbnail={getOptimisedImageUrl(image, productImage / 10)}
          alt={title}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        />
      </Box>

      <Box __css={mergedStyles.content}>
        {reviews?.average ? (
          <Reviews
            reviews={reviews}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          />
        ) : null}

        {title ? (
          <Box
            as="h4"
            __css={mergedStyles.CardTitle}
            className="bundle-product-card__title"
            role="button"
            onClick={() => handleClick(titleRef)}
            onKeyDown={(e) => handleKeyDown(e, titleRef)}
            tabIndex={0}
            ref={titleRef}
          >
            {title}
          </Box>
        ) : null}

        {variantTitle ? (
          <Box
            as="h5"
            __css={mergedStyles.CardSubTitle}
            role="button"
            className="bundle-product-card__subtitle"
            onClick={() => handleClick(variantTitleRef)}
            onKeyDown={(e) => handleKeyDown(e, variantTitleRef)}
            tabIndex={0}
            ref={variantTitleRef}
          >
            {variantTitle}
          </Box>
        ) : null}
        {subtitle ? (
          <Box
            as={H5}
            __css={mergedStyles.CardSubSubTitle}
            className="bundle-product-card__subtitle"
            modifiers={['body', 'subdued']}
            onClick={() => handleClick(subtitleRef)}
            onKeyDown={(e) => handleKeyDown(e, subtitleRef)}
            tabIndex={0}
            ref={subtitleRef}
          >
            {subtitle}
          </Box>
        ) : null}
      </Box>
      {children}
      {VariantSelector ? VariantSelector : null}
      {onIncrease && onDecrease && (
        <Box
          className={`${
            available
              ? 'bundle-product-card__quantity-button-container'
              : 'bundle-product-card__quantity-button-container bundle-product-card__quantity-button-container--unavailable'
          }`}
          __css={mergedStyles.QuantityButtonContainer}
          data-available={available}
          role="group"
        >
          <Box
            as="button"
            role="button"
            __css={mergedStyles.QuantityButton}
            className="bundle-product-card__quantity-button-container--button"
            data-testid="decrement-quantity"
            disabled={
              !available ||
              (mergedStyles.showAddButtonForZeroQuantity && quantity == 0)
            }
            onClick={onDecrease}
            aria-label={t('aria_labels.decrease_quantity')}
            tabIndex={0}
          >
            <CustomMinusIcon __css={mergedStyles.CustomMinusIcon} />
          </Box>
          {available &&
          quantity === 0 &&
          mergedStyles.showAddButtonForZeroQuantity ? (
            <Box
              as="button"
              className="bundle-product-card__add-button"
              data-testid="add-button"
              __css={mergedStyles.AddButton}
              onClick={onIncrease}
              aria-label={t('aria_labels.add_product', { title })}
              tabIndex={0}
            >
              {mergedStyles.addButtonText || t('product_swap.add')}
            </Box>
          ) : (
            <Box
              as="span"
              className="bundle-product-card__quantity"
              data-testid="product-quantity"
              __css={mergedStyles.Quantity}
            >
              {available ? quantity : t('product_swap.sold_out')}
            </Box>
          )}
          <Box
            as="button"
            __css={mergedStyles.QuantityButton}
            className="bundle-product-card__quantity-button-container--button"
            data-testid="increment-quantity"
            role="button"
            disabled={
              !available ||
              !isThereEnoughInventory({
                inventory: productVariantInventory,
                quantity,
              }) ||
              (mergedStyles.showAddButtonForZeroQuantity && quantity == 0)
            }
            onClick={onIncrease}
            aria-label={t('aria_labels.increase_quantity')}
            tabIndex={0}
          >
            <CustomPlusIcon __css={mergedStyles.CustomPlusIcon} />
          </Box>
        </Box>
      )}

      {onChoose ? (
        <Box
          as="button"
          role="button"
          aria-label={t('aria_labels.choose_button', { title })}
          __css={mergedStyles.CardButton}
          className="bundle-product-card__button"
          onClick={() => onChoose(id)}
          disabled={quantity <= 0}
          data-testid="choose-button"
          tabIndex={0}
        >
          {quantity <= 0
            ? t('product_swap.sold_out')
            : onChooseText
              ? onChooseText
              : t('product_swap.choose')}
        </Box>
      ) : null}
      {price ? (
        <Box
          as="div"
          __css={mergedStyles.CardPrice}
          className="bundle-product-card__price"
        >
          <PriceSection
            price={price}
            currentPrice={currentPrice}
            currency={currency}
            bundleSetting={bundleSetting}
            doNotApplyDiscount={doNotApplyDiscount}
          />
        </Box>
      ) : null}
    </Box>
  );
};

interface IGetPrice {
  price: string | number;
  currentPrice?: number;
  currency?: string;
  bundleSetting?: BundleSetting;
  doNotApplyDiscount?: boolean;
  styles?: any;
}

const PriceSection: React.FunctionComponent<IGetPrice> = ({
  price,
  currentPrice,
  currency,
  bundleSetting,
  doNotApplyDiscount = false,
}): ReactElement => {
  const format = getCurrencyFormatter(currency);
  const formattedPrice = format(currentPrice as number);
  const priceWithDiscount = doNotApplyDiscount
    ? price
    : applyDiscountsToLine({
        price,
        quantity: 1,
        discounts: [bundleSetting].filter(Boolean),
      });

  return (
    <>
      <span className="current-price">{format(priceWithDiscount)} </span>
      {currentPrice && currentPrice > priceWithDiscount ? (
        <span
          className="original-price"
          style={{ textDecoration: 'line-through' }}
        >
          {formattedPrice}
        </span>
      ) : null}
    </>
  );
};

export default ThemedProductCard;
